import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  CalendarDays,
  Building2,
  Hotel,
  Users,
  UserCog,
  BedDouble,
  Plane,
  Bus,
  QrCode,
  Luggage,
  BarChart3,
  Bell,
  LogOut,
  Menu,
  ClipboardList,
  ChevronDown,
  Shield,
  Eye,
  ScanLine,
  MessageSquare,
  ShieldCheck,
  Settings2,
} from 'lucide-react';
import Logo from './Logo';
import { useAuth, ROLES } from '../context/AuthContext';
import { useEvents } from '../context/EventContext';
import { get } from '../api/client';
import { Avatar, Badge } from './UI';

/* Navigation per role — the sidebar is generated from these definitions. */
const NAV = {
  [ROLES.SUPER_ADMIN]: [
    { section: 'Overview', items: [
      { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/admin/events', label: 'Events', icon: CalendarDays },
    ]},
    { section: 'Directory', items: [
      { to: '/admin/corporates', label: 'Corporates', icon: Building2 },
      { to: '/admin/hotels', label: 'Hotels', icon: Hotel },
      { to: '/admin/participants', label: 'Participants', icon: Users },
      { to: '/admin/staff', label: 'Staff & Portals', icon: UserCog },
    ]},
    { section: 'Operations', items: [
      { to: '/admin/scan', label: 'Scan desk', icon: ScanLine },
      { to: '/admin/rooms', label: 'Room Allocation', icon: BedDouble },
      { to: '/admin/flights', label: 'Flights & Travel', icon: Plane },
      { to: '/admin/transport', label: 'Bus & Transport', icon: Bus },
      { to: '/admin/luggage', label: 'Luggage', icon: Luggage },
      { to: '/admin/attendance', label: 'Attendance', icon: QrCode },
    ]},
    { section: 'Engagement', items: [
      { to: '/admin/registrations', label: 'Registrations', icon: ClipboardList },
      { to: '/admin/notifications', label: 'Communication', icon: Bell },
      { to: '/admin/messages', label: 'Messages', icon: MessageSquare },
      { to: '/admin/reports', label: 'Reports', icon: BarChart3 },
    ]},
    { section: 'Platform', items: [
      { to: '/admin/settings', label: 'Settings', icon: Settings2 },
    ]},
  ],
  [ROLES.CORPORATE_ADMIN]: [
    { section: 'Overview', items: [
      { to: '/corporate', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/corporate/events', label: 'Events', icon: CalendarDays },
    ]},
    { section: 'My People', items: [
      { to: '/corporate/participants', label: 'Employees', icon: Users },
      { to: '/corporate/rooms', label: 'Room Partners', icon: BedDouble },
    ]},
    { section: 'Operations', items: [
      { to: '/corporate/flights', label: 'Travel Readiness', icon: Plane },
      { to: '/corporate/transport', label: 'Transport', icon: Bus },
      { to: '/corporate/attendance', label: 'Attendance', icon: QrCode },
    ]},
    { section: 'Insights', items: [
      { to: '/corporate/notifications', label: 'Communication', icon: Bell },
      { to: '/corporate/messages', label: 'Messages', icon: MessageSquare },
      { to: '/corporate/reports', label: 'Reports', icon: BarChart3 },
    ]},
  ],
  [ROLES.STAFF]: [
    { section: 'Overview', items: [
      { to: '/staff', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/staff/events', label: 'My Events', icon: CalendarDays },
    ]},
    { section: 'On Site', items: [
      { to: '/staff/scan', label: 'Scan desk', icon: ScanLine },
      { to: '/staff/attendance', label: 'Attendance', icon: QrCode },
      { to: '/staff/transport', label: 'Bus & Transport', icon: Bus },
      { to: '/staff/luggage', label: 'Luggage', icon: Luggage },
      { to: '/staff/rooms', label: 'Room Allocation', icon: BedDouble },
    ]},
    { section: 'Event', items: [
      { to: '/staff/participants', label: 'Participants', icon: Users },
      { to: '/staff/flights', label: 'Flights & Travel', icon: Plane },
      { to: '/staff/notifications', label: 'Communication', icon: Bell },
      { to: '/staff/messages', label: 'Messages', icon: MessageSquare },
      { to: '/staff/reports', label: 'Reports', icon: BarChart3 },
    ]},
  ],
  [ROLES.USER]: [
    { section: 'My Event', items: [
      { to: '/attendee', label: 'Home', icon: LayoutDashboard, end: true },
      { to: '/attendee/schedule', label: 'Schedule', icon: CalendarDays },
      { to: '/attendee/check-in', label: 'Check in', icon: ScanLine },
      { to: '/attendee/pass', label: 'Digital pass', icon: QrCode },
    ]},
    { section: 'Logistics', items: [
      { to: '/attendee/travel', label: 'Travel', icon: Plane },
      { to: '/attendee/stay', label: 'Stay', icon: BedDouble, modules: ['hotelBooking'] },
      { to: '/attendee/transport', label: 'Transport', icon: Bus, modules: ['buses'] },
      { to: '/attendee/luggage', label: 'Luggage', icon: Luggage, modules: ['onboardingLuggage', 'arrivalLuggage'] },
    ]},
    { section: 'Account', items: [
      { to: '/attendee/inbox', label: 'Inbox', icon: Bell },
      { to: '/attendee/declaration', label: 'Health details', icon: ShieldCheck },
      { to: '/attendee/profile', label: 'Profile', icon: UserCog },
    ]},
  ],
  [ROLES.EVENT_VIEWER]: [
    { section: 'Overview', items: [
      { to: '/viewer', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/viewer/participants', label: 'Participants', icon: Users },
    ]},
    { section: 'Event', items: [
      { to: '/viewer/attendance', label: 'Attendance', icon: QrCode },
      { to: '/viewer/transport', label: 'Bus & Transport', icon: Bus },
      { to: '/viewer/luggage', label: 'Luggage', icon: Luggage },
      { to: '/viewer/rooms', label: 'Rooms', icon: BedDouble },
      { to: '/viewer/flights', label: 'Flights & Travel', icon: Plane },
      { to: '/viewer/reports', label: 'Reports', icon: BarChart3 },
    ]},
  ],
  [ROLES.HOTEL]: [
    { section: 'Property', items: [
      { to: '/hotel', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/hotel/rooms', label: 'Rooms', icon: BedDouble },
      { to: '/hotel/checkin', label: 'Check-In Desk', icon: QrCode },
      { to: '/hotel/reports', label: 'Occupancy Report', icon: BarChart3 },
    ]},
  ],
};

const ROLE_LABEL = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.CORPORATE_ADMIN]: 'Corporate Admin',
  [ROLES.HOTEL]: 'Hotel Partner',
  [ROLES.STAFF]: 'Event Staff',
  [ROLES.EVENT_VIEWER]: 'Company Observer',
  [ROLES.USER]: 'Attendee',
};

export default function Layout({ gate: Gate }) {
  const { user, logout, isViewer } = useAuth();
  const { hasModule } = useEvents();
  const isAttendee = user?.role === ROLES.USER;
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  // Items tied to a module only show when the selected event uses it.
  const sections = (NAV[user?.role] || [])
    .map((sec) => ({ ...sec, items: sec.items.filter((i) => !i.modules || i.modules.some((m) => hasModule(m))) }))
    .filter((sec) => sec.items.length);

  /* Close the drawer whenever the route changes. */
  useEffect(() => { setMobileOpen(false); setMenuOpen(false); }, [location.pathname]);

  /* Poll the unread badge — cheap, and keeps the bell honest. */
  useEffect(() => {
    let alive = true;
    const load = () => get('/notifications/unread-count')
      .then((d) => alive && setUnread(d.count))
      .catch(() => {});
    load();
    const id = setInterval(load, 45000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const sidebar = (
    <>
      <div style={{ padding: '20px 18px 16px', borderBottom: '1px solid var(--border)' }}>
        <Logo size={36} showText />
      </div>

      <nav style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>
        {sections.map((sec) => (
          <div key={sec.section} style={{ marginBottom: 20 }}>
            <p style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase',
              color: 'var(--text-lo)', padding: '0 10px', marginBottom: 7,
            }}>
              {sec.section}
            </p>
            {sec.items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                style={({ isActive }) => ({
                  position: 'relative',
                  display: 'flex', alignItems: 'center', gap: 11,
                  padding: '9px 11px', marginBottom: 2, borderRadius: 'var(--r-sm)',
                  fontSize: 13.2, fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--gold-300)' : 'var(--text-mid)',
                  background: isActive ? 'rgba(239,212,138,0.09)' : 'transparent',
                  transition: 'color 0.16s, background 0.16s',
                })}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="gl-nav-bar"
                        transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                        style={{
                          position: 'absolute', left: -12, top: 7, bottom: 7, width: 3,
                          background: 'var(--gold-400)', borderRadius: '0 3px 3px 0',
                        }}
                      />
                    )}
                    <item.icon size={16.5} style={{ flexShrink: 0 }} />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px' }}>
          <Avatar name={user?.name} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.8, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-lo)' }}>{ROLE_LABEL[user?.role]}</div>
          </div>
          <button onClick={() => { logout(); nav('/login'); }} title="Sign out"
            style={{ color: 'var(--text-lo)', display: 'flex', padding: 6, borderRadius: 6 }}>
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className={isAttendee ? 'gl-has-bottombar' : undefined} style={{ display: 'flex', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {/* desktop sidebar */}
      <aside className="gl-sidebar" style={{
        width: 'var(--sidebar-w)', flexShrink: 0,
        background: 'var(--ink-850)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, height: '100vh',
      }}>
        {sidebar}
      </aside>

      {/* mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(4,4,6,0.7)', zIndex: 800, backdropFilter: 'blur(3px)' }}
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 360, damping: 34 }}
              style={{
                position: 'fixed', top: 0, left: 0, bottom: 0, width: 268, zIndex: 810,
                background: 'var(--ink-850)', borderRight: '1px solid var(--border)',
                display: 'flex', flexDirection: 'column',
              }}
            >
              {sidebar}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* main column */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <header style={{
          height: 'var(--topbar-h)', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 14, padding: '0 22px',
          borderBottom: '1px solid var(--border)',
          background: 'rgba(11,11,12,0.82)', backdropFilter: 'blur(14px)',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <button className="gl-burger" onClick={() => setMobileOpen(true)}
            style={{ display: 'none', color: 'var(--text-mid)', padding: 6 }} aria-label="Open menu">
            <Menu size={20} />
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 15.5, fontWeight: 600 }}>{pageTitle(location.pathname)}</h1>
          </div>

          <NavLink to={isAttendee ? '/attendee/inbox' : `${homeFor(user?.role)}/notifications`}
            style={{ position: 'relative', display: 'flex', padding: 8, borderRadius: 8, color: 'var(--text-mid)' }}>
            <Bell size={18} />
            {unread > 0 && (
              <motion.span
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                style={{
                  position: 'absolute', top: 3, right: 3, minWidth: 16, height: 16, padding: '0 4px',
                  borderRadius: 99, background: 'var(--gold-400)', color: 'var(--ink-900)',
                  fontSize: 9.5, fontWeight: 700, display: 'grid', placeItems: 'center',
                }}
              >
                {unread > 99 ? '99+' : unread}
              </motion.span>
            )}
          </NavLink>

          <div style={{ position: 'relative' }}>
            <button onClick={() => setMenuOpen((v) => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 9px 5px 5px', borderRadius: 99, border: '1px solid var(--border)' }}>
              <Avatar name={user?.name} size={26} />
              <ChevronDown size={13} color="var(--text-lo)" />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.97 }}
                  transition={{ duration: 0.16 }}
                  style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 230,
                    background: 'var(--ink-800)', border: '1px solid var(--border)',
                    borderRadius: 'var(--r)', boxShadow: 'var(--shadow)', overflow: 'hidden', zIndex: 200,
                  }}
                >
                  <div style={{ padding: '13px 15px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 2 }}>{user?.email}</div>
                    <Badge tone="gold" style={{ marginTop: 8 }}>
                      <Shield size={10} /> {ROLE_LABEL[user?.role]}
                    </Badge>
                    {(user?.corporate?.name || user?.hotel?.name) && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-mid)', marginTop: 8 }}>
                        {user?.corporate?.name || user?.hotel?.name}
                      </div>
                    )}
                  </div>
                  <button onClick={() => { logout(); nav('/login'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                      padding: '11px 15px', fontSize: 13, color: 'var(--danger)', textAlign: 'left',
                    }}>
                    <LogOut size={15} /> Sign out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        {isViewer && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '9px 22px', fontSize: 12.5, color: 'var(--text-mid)',
            background: 'rgba(239,212,138,0.06)', borderBottom: '1px solid var(--border)',
          }}>
            <Eye size={14} color="var(--gold-500)" style={{ flexShrink: 0 }} />
            <span>View-only access — you can see everything for your event, but nothing here can be changed.</span>
          </div>
        )}

        <main style={{ flex: 1, padding: '24px 22px 48px', maxWidth: 1500, width: '100%' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
            >
              {Gate ? <Gate><Outlet /></Gate> : <Outlet />}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* On phones an attendee gets the app's bottom bar, scanner raised in the middle. */}
        {isAttendee && (
          <nav className="gl-bottombar" aria-label="Quick navigation">
            {[
              { to: '/attendee', label: 'Home', icon: LayoutDashboard, end: true },
              { to: '/attendee/schedule', label: 'Schedule', icon: CalendarDays },
              { to: '/attendee/check-in', label: 'Scan', icon: ScanLine, raised: true },
              { to: '/attendee/pass', label: 'Pass', icon: QrCode },
              { to: '/attendee/inbox', label: 'Inbox', icon: Bell },
            ].map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) => `gl-bb-item${isActive ? ' is-active' : ''}${item.raised ? ' is-raised' : ''}`}>
                <span className="gl-bb-icon"><item.icon size={item.raised ? 24 : 19} /></span>
                {!item.raised && <span className="gl-bb-label">{item.label}</span>}
              </NavLink>
            ))}
          </nav>
        )}
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .gl-sidebar { display: none !important; }
          .gl-burger { display: flex !important; }
        }
      `}</style>
    </div>
  );
}

function homeFor(role) {
  if (role === ROLES.CORPORATE_ADMIN) return '/corporate';
  if (role === ROLES.HOTEL) return '/hotel';
  if (role === ROLES.STAFF) return '/staff';
  if (role === ROLES.EVENT_VIEWER) return '/viewer';
  if (role === ROLES.USER) return '/attendee';
  return '/admin';
}

/** Derives a readable page title from the current path. */
function pageTitle(path) {
  // Both '/staff' (the staff portal home) and '/admin/staff' end in "staff".
  if (path === '/staff' || path === '/viewer') return 'Dashboard';
  if (path === '/attendee') return 'Home';
  const last = path.split('/').filter(Boolean).pop() || '';
  const map = {
    admin: 'Dashboard', corporate: 'Dashboard', hotel: 'Dashboard',
    events: 'Events', corporates: 'Corporate Accounts', hotels: 'Hotels',
    participants: 'Participants', staff: 'Staff & Portal Accounts',
    rooms: 'Room Allocation', flights: 'Flights & Travel', transport: 'Bus & Transport',
    luggage: 'Luggage Tracking', attendance: 'Attendance', registrations: 'Registrations',
    notifications: 'Communication', reports: 'Reports & Analytics', checkin: 'Check-In Desk', scan: 'Scan Desk',
    schedule: 'Schedule', pass: 'Digital Pass', 'check-in': 'Check In', travel: 'Travel',
    stay: 'Your Stay', inbox: 'Inbox', profile: 'Profile', messages: 'Messages', settings: 'Settings', declaration: 'Health Details',
  };
  if (/^[0-9a-f]{24}$/i.test(last)) return 'Event Details';
  return map[last] || 'Golden Lotus';
}
